import React, { useContext, useState } from 'react';
import { Eye, EyeOff, LockKeyhole } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { showError, showSuccess } from '../services/toastService';
import { AuthContext } from '../context/AuthContext';
import './AdminLogin.css';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { setAuthData } = useContext(AuthContext);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    if (errors[name]) setErrors((previous) => ({ ...previous, [name]: '' }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!formData.email) nextErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) nextErrors.email = 'Enter a valid email';
    if (!formData.password) nextErrors.password = 'Password is required';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const response = await authService.login(formData.email.trim(), formData.password);
      if (response.success) {
        setAuthData(response.data.token, response.data.user);
        showSuccess('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      showError(error.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-brand-strip" aria-hidden="true" />
      <main className="admin-login-card">
        <div className="admin-login-header">
          <img src="/logo.png" alt="KeyShop" />
          <div className="admin-login-title">
            <h1>KeyShop</h1>
            <p>Marketplace Admin</p>
          </div>
        </div>

        <div className="admin-login-intro">
          <span><LockKeyhole size={16} /></span>
          <div><strong>Admin sign in</strong><p>Manage buyers, orders and marketplace operations.</p></div>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form" noValidate>
          <div className="admin-form-group">
            <label htmlFor="email">Email address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder="admin@keyshop.com"
              autoComplete="email"
              autoFocus
            />
            {errors.email && <span className="admin-error-text">{errors.email}</span>}
          </div>

          <div className="admin-form-group">
            <label htmlFor="password">Password</label>
            <div className="admin-password-field">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'error' : ''}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
              <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
            {errors.password && <span className="admin-error-text">{errors.password}</span>}
          </div>

          <button type="submit" className="admin-login-button" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <footer>Authorized KeyShop administrators only</footer>
      </main>
    </div>
  );
};

export default AdminLogin;
